import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStrings } from '../i18n';
import useOrderPolling from '../hooks/useOrderPolling';
import { getOrderById } from '../services/orderService';
import ShipmentStatus from '../components/ShipmentStatus';
import PanchTatvaButton from '../components/PanchTatvaButton';

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const strings = useStrings();
  const od = strings.orderDetails;
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  // Fetch order from MongoDB backend
  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        const idToken = await getIdToken();
        const data = await getOrderById(orderId, idToken);
        if (!data) {
          setError(od.orderNotFound);
          return;
        }
        setOrder({
          ...data,
          date: data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt),
          timeline: data.timeline || buildTimeline(data.status, data.createdAt),
        });
      } catch (err) {
        console.error('Failed to load order:', err);
        setError(od.failedToLoad);
      }
    })();
  }, [orderId, getIdToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling — keep order fresh until shipment_id appears
  const hasShipment = Boolean(order?.shiprocket?.shipment_id);
  const { isPolling, pollingSeconds } = useOrderPolling(orderId, null, {
    enabled: Boolean(order && !hasShipment),
    getIdToken,
    onShipmentReady: (updated) =>
      setOrder((prev) => ({
        ...prev,
        ...updated,
        date: prev?.date || new Date(updated.createdAt),
        timeline: prev?.timeline || buildTimeline(updated.status, updated.createdAt),
      })),
    onPermanentFail: (updated) =>
      setOrder((prev) => ({ ...prev, ...updated })),
  });

  // Generate a basic timeline based on status when not stored
  const buildTimeline = (status, createdAt) => {
    const date = createdAt instanceof Date ? createdAt : new Date(createdAt);
    const steps = ['order-placed', 'confirmed', 'shipped', 'out-for-delivery', 'delivered'];
    const labels = {
      'order-placed': od.orderConfirmedStep || 'Order Placed',
      confirmed: od.orderConfirmedStep || 'Order Confirmed',
      shipped: od.shippedStep || 'Shipped',
      'out-for-delivery': od.outForDeliveryStep || 'Out for Delivery',
      delivered: od.deliveredStep || 'Delivered',
    };
    const statusIndex = steps.indexOf(status === 'processing' ? 'confirmed' : status);
    return steps.map((step, i) => ({
      status: step,
      label: labels[step],
      date: i === 0 ? date : null,
      time: i === 0 ? date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null,
      completed: i <= statusIndex,
    }));
  };

  if (!order && !error) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 text-center">
        <p className="font-sans text-bodyText">{od.loadingOrder}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 text-center">
        <p className="font-sans text-red-500 mb-6">{error}</p>
        <button onClick={() => navigate('/orders')} className="font-sans text-secondary underline">
          {od.backToOrders}
        </button>
      </div>
    );
  }

  const formatDate = date => {
    if (!date) return '';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = status => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'out-for-delivery':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/orders')}
          className="p-2 hover:bg-cardBg rounded-lg transition"
        >
          <svg className="w-6 h-6 text-bodyText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-3xl md:text-4xl font-heading text-headingText">{od.title} {orderId}</h1>
          <p className="font-sans text-sm text-textSecondary">{od.orderedOn} {formatDate(order.date)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 md:p-8">
            <h2 className="text-2xl font-heading text-headingText mb-8">{od.orderTimeline}</h2>

            <div className="relative">
              {order.timeline.map((step, index) => (
                <div key={step.status} className="flex gap-4 pb-8 last:pb-0">
                  {/* Timeline Circle */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm font-sans ${
                        step.completed
                          ? 'bg-green-100 text-green-700 border-green-400'
                          : 'bg-surface text-textSecondary border-surface'
                      }`}
                    >
                      {step.completed ? '✓' : index + 1}
                    </div>
                    {index < order.timeline.length - 1 && (
                      <div
                        className={`w-0.5 h-16 my-2 ${step.completed ? 'bg-green-400' : 'bg-surface'}`}
                      />
                    )}
                  </div>

                  {/* Timeline Content */}
                  <div className="pt-2">
                    <p className="font-sans font-semibold text-bodyText">{step.label}</p>
                    {step.date && (
                      <p className="font-sans text-sm text-textSecondary">
                        {formatDate(step.date)} at {step.time}
                      </p>
                    )}
                    {!step.date && (
                      <p className="font-sans text-sm text-textSecondary italic">Pending</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Live Shipment Status */}
          <ShipmentStatus
            order={order}
            isPolling={isPolling}
            pollingSeconds={pollingSeconds}
            onContactSupport={() => navigate('/contact')}
          />

          {/* Items */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 md:p-8">
            <h2 className="text-2xl font-heading text-headingText mb-6">{od.itemsOrdered}</h2>

            <div className="space-y-4">
              {(order.items || []).map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-primaryBg rounded-xl border-l-4 border-secondary gap-3"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-surface rounded-xl flex items-center justify-center flex-shrink-0">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        <span className="font-sans text-xs text-textSecondary">–</span>
                      )}
                    </div>
                    <div>
                      <p className="font-sans font-semibold text-bodyText">{item.name}</p>
                      <p className="font-sans text-sm text-textSecondary">{od.quantity}: {item.quantity}</p>
                      <p className="font-sans text-sm text-textSecondary">{od.price}: ₹{item.price}</p>
                    </div>
                  </div>
                  <p className="font-bold text-secondary text-lg self-end sm:self-auto">₹{item.price * item.quantity}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 md:p-8">
            <h2 className="text-2xl font-heading text-headingText mb-6">{od.shippingAddress}</h2>

            <div className="p-4 bg-primaryBg rounded-xl">
              <p className="font-sans font-semibold text-bodyText mb-1">
                {order.shippingAddress?.fullName || order.shippingAddress?.name}
              </p>
              <p className="font-sans text-sm text-bodyText mb-1">{order.shippingAddress?.phone}</p>
              <p className="font-sans text-sm text-bodyText">{order.shippingAddress?.address}</p>
              <p className="font-sans text-sm text-bodyText">
                {order.shippingAddress?.city}, {order.shippingAddress?.state}
              </p>
              <p className="font-sans text-sm text-bodyText">{order.shippingAddress?.pincode}</p>
            </div>
          </div>
        </div>

        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <div className="sticky top-32 space-y-6">
            {/* Order Summary */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6">
              <h3 className="text-xl font-heading text-headingText mb-4">{od.orderSummary}</h3>

              <div className="space-y-3 pb-4 border-b border-surface">
                <div className="flex justify-between font-sans text-sm text-bodyText">
                  <span>{od.subtotal}</span>
                  <span>₹{order.pricing?.subtotal}</span>
                </div>
                <div className="flex justify-between font-sans text-sm text-bodyText">
                  <span>{od.shipping}</span>
                  <span>{order.pricing?.shipping === 0 ? od.free : `₹${order.pricing?.shipping}`}</span>
                </div>
                <div className="flex justify-between font-sans text-sm text-bodyText">
                  <span>{od.tax}</span>
                  <span>₹{order.pricing?.tax}</span>
                </div>
              </div>

              <div className="flex justify-between font-sans text-base font-bold text-secondary pt-4">
                <span>{od.total}</span>
                <span>₹{order.pricing?.total}</span>
              </div>
            </div>

            {/* Status Badge */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 text-center">
              <p className="font-sans text-xs text-textSecondary mb-2 uppercase">{od.orderStatus}</p>
              <span
                className={`inline-block px-4 py-2 rounded-full font-sans text-sm font-bold ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status.charAt(0).toUpperCase() +
                  order.status.slice(1).replace('-', ' ')}
              </span>
            </div>

            {/* Actions */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6 space-y-3">
              <PanchTatvaButton variant="primary" fullWidth onClick={() => navigate(`/orders/${orderId}/receipt`)}>
                {od.viewReceipt}
              </PanchTatvaButton>
              <PanchTatvaButton variant="outline" fullWidth>
                {od.reorder}
              </PanchTatvaButton>
            </div>

            {/* Support */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6">
              <h3 className="font-sans font-semibold text-bodyText mb-2">{od.needHelp}</h3>
              <p className="font-sans text-sm text-textSecondary mb-3">{od.helpSupport}</p>
              <PanchTatvaButton
                variant="outline"
                fullWidth
                onClick={() => navigate('/contact')}
              >
                {od.contactSupport}
              </PanchTatvaButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
